import { API_ENDPOINTS } from '../config/api'
import { getActiveOrgId } from '../utils/orgUtils'

export type PostureReadinessStatus = 'READY' | 'IN_PROGRESS' | 'BLOCKED' | 'NOT_STARTED'
export type RuntimeDriftLevel = 'LOW' | 'MEDIUM' | 'HIGH'
export type TimelineEventType =
  | 'EVIDENCE'
  | 'REMEDIATION'
  | 'AUDIT'
  | 'GOVERNANCE'
  | 'SCOPE'
  | 'READINESS'
export type TimelineSeverity = 'INFO' | 'WARNING' | 'CRITICAL'

export type PostureBlockReason = {
  code: string
  count: number
  label: string
}

export type PostureMatrixRow = {
  standardCode: string
  standardTitle: string
  selfAssessedPercentage: number
  verifiedPercentage: number | null
  readinessStatus: PostureReadinessStatus
  readinessPercentage: number | null
  compactBlockReason?: string | null
  blockReasons?: PostureBlockReason[]
  openGaps: number
  runtimeDrift: RuntimeDriftLevel
}

export type PostureMatrixResponse = {
  rows: PostureMatrixRow[]
  generatedAt?: string
}

export type RuntimeTimelineEvent = {
  id: string
  type: TimelineEventType
  title: string
  systemName?: string
  standardCode?: string
  severity?: TimelineSeverity
  createdAt: string
}

export type RuntimeTimelineResponse = {
  events: RuntimeTimelineEvent[]
  generatedAt?: string
}

function withOrganizationScope(url: string): string {
  const orgId = getActiveOrgId()
  if (!orgId) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}organization_id=${encodeURIComponent(orgId)}`
}

function getAuthHeaders(): HeadersInit {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('token') || localStorage.getItem('accessToken')
      : null
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' }
}

async function parseJson(res: Response) {
  const body = await res.json()
  if (!res.ok) throw new Error(body?.message || body?.error || 'Request failed')
  return body?.data ?? body
}

export async function fetchExecutivePostureMatrix(standardCodes: string[] = []) {
  const params = new URLSearchParams()
  if (standardCodes.length) params.set('standards', standardCodes.join(','))
  const q = params.toString() ? `?${params.toString()}` : ''
  const res = await fetch(withOrganizationScope(`${API_ENDPOINTS.EXECUTIVE_POSTURE_MATRIX}${q}`), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as PostureMatrixResponse
}

export async function fetchExecutiveRuntimeTimeline(limit = 15) {
  const params = new URLSearchParams()
  params.set('limit', String(limit))
  const res = await fetch(
    withOrganizationScope(`${API_ENDPOINTS.EXECUTIVE_RUNTIME_TIMELINE}?${params.toString()}`),
    { headers: getAuthHeaders() },
  )
  return parseJson(res) as RuntimeTimelineResponse
}
